import { useEffect, useRef } from 'react'
import { useAuthStore } from '../stores/auth'
import { presenceApi } from '../api/endpoints'

const HEARTBEAT_INTERVAL_MS = 60_000

/**
 * Sends periodic presence heartbeats while the user is authenticated.
 *
 * Behavior:
 *  - Generates a random `tab_id` on mount for multi-tab deduplication.
 *  - Sends an immediate heartbeat on mount, then every 60 seconds.
 *  - Listens to the Page Visibility API: when the tab becomes hidden or
 *    visible, sends an immediate heartbeat with the correct `is_active` flag.
 *  - Silently ignores network errors (heartbeats are best-effort).
 *  - Cleans up interval and listener on unmount.
 */
export function usePresenceHeartbeat() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const tabIdRef = useRef(crypto.randomUUID().slice(0, 12))

  useEffect(() => {
    if (!isAuthenticated) return

    const tabId = tabIdRef.current

    function sendHeartbeat() {
      const isActive = document.visibilityState === 'visible'
      presenceApi
        .heartbeat({ device_type: 'web', is_active: isActive, tab_id: tabId })
        .catch(() => {
          // Best-effort — swallow network errors silently
        })
    }

    // Send immediately on mount
    sendHeartbeat()

    // Then every 60 seconds
    const intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)

    // On tab visibility change, send an immediate heartbeat
    function handleVisibilityChange() {
      sendHeartbeat()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated])
}
